import { Menu, MenuItem } from '@components/std-components/Menu'
import { useLocalStore, useObserver } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo } from 'react'
import { StickyTree } from 'react-virtualized-sticky-tree'
import { TreeNavStore } from '../store'
import { LOADER_NODE_LIST } from './loader-data'
import { CATEGORY_INTERNAL_SEPERATOR } from '@components/UI-Components/HierarchyWiseItems/constants'
import { makeStyles } from '@material-ui/core'
import NavNodeName from './NavNodeName'
import classNames from 'classnames'

interface ITreeNav {
  nodeList: any
  treeMaxDepth?: number
  selected: string[]
  showOnlySelected?: boolean
  onNavClick: ({ id, leafNodeId }) => void
  height?: number
  showSelectedIcon?: boolean
  scrollIndex?: number
  showLoading?: boolean
  isEnhancedViewEnabled?: boolean
  enhancedHeaderEnabled?: boolean
}

export const SelectedIcon = React.memo(() => {
  return (
    <div
      style={{
        width: 3,
        height: 18,
        background: '#81c8ff',
        marginLeft: -9,
        marginTop: -1,
        position: 'absolute'
      }}></div>
  )
})

export const EnhancedSelectedIcon = React.memo(() => {
  return (
    <div
      style={{
        width: 3,
        height: 24,
        background: '#000000',
        marginLeft: -9,
        position: 'absolute'
      }}></div>
  )
})

const useStyles = makeStyles(theme => ({
  stickyRow: {
    backgroundColor: '#fbfbfb',
    marginTop: 6
  },
  enhancedStickyRow: {
    backgroundColor: 'white',
    marginTop: 0,
    paddingTop: 10,
    '& .menu-title': {
      marginTop: 0
    }
  }
}))

export const TreeNav: React.FC<ITreeNav> = ({
  nodeList,
  treeMaxDepth = 10,
  selected,
  showOnlySelected = true,
  onNavClick,
  height,
  showSelectedIcon = false,
  showLoading,
  isEnhancedViewEnabled
}): JSX.Element => {
  return useObserver(function useHook() {
    const classes = useStyles({ isEnhancedViewEnabled })
    const navItemHeight = isEnhancedViewEnabled ? 35 : 30
    const {
      setTreeRef,
      scrollIndex,
      getLeafNode,
      selectedTree,
      setSelectedTree,
      selectedTreeSet
    } = useLocalStore(() => new TreeNavStore())
    useEffect(() => {
      setSelectedTree(selected)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected])

    /**
     * Helper method to check wether the given node item is sticky or not
     * It checks every descendent is part of selected tree
     */
    const isItemSticky = useCallback(
      (nodes, childId, isParentSelected) => {
        const tempSticky = isParentSelected && selectedTreeSet.has(nodes[childId]?.name)
        if (isEnhancedViewEnabled) {
          const hasChildItems = !!nodes[childId]?.children?.length
          return hasChildItems
        }
        return tempSticky
      },
      [selectedTreeSet]
    )

    /**
     * Helper method to check wether the given node item is Selected
     */
    const isItemNodeSelected = useCallback(
      (nodes, childId, isParentSelected) => {
        const tempSticky = isParentSelected && selectedTreeSet.has(nodes[childId]?.name)
        const allHierarchy = childId.split(CATEGORY_INTERNAL_SEPERATOR)
        return tempSticky && allHierarchy.join(',') === selectedTree.join(',')
      },
      [selectedTreeSet, selectedTree]
    )

    const getChildren = useMemo(
      () => id => {
        let nodes = nodeList
        if (showLoading && !nodes?.root?.children?.length) {
          nodes = LOADER_NODE_LIST
        }
        if (nodes?.[id]?.children) {
          const isParentSelected = showLoading
            ? false
            : id === 'root' || selectedTreeSet.has(nodes[id].name)
          return nodes[id].children.map(childId => ({
            id: childId,
            height: showOnlySelected
              ? isParentSelected
                ? navItemHeight
                : 0
              : navItemHeight,
            isSticky: isItemSticky(nodes, childId, isParentSelected),
            stickyTop:
              navItemHeight * nodes?.[childId]?.depth +
              (nodes[childId]?.children?.length ? 0 : navItemHeight),
            zIndex: treeMaxDepth + 1 - nodes?.[childId]?.depth,
            node: nodes?.[childId],
            isItemSelected: isItemNodeSelected(nodes, childId, isParentSelected)
          }))
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [nodeList, showLoading, selectedTreeSet.toString(), navItemHeight]
    )
    const getLoaderChildren = useMemo(
      () => id => {
        let nodes = LOADER_NODE_LIST
        if (nodes?.[id]?.children) {
          return nodes[id].children.map(childId => ({
            id: childId,
            height: navItemHeight,
            isSticky: false,
            stickyTop: navItemHeight * nodes?.[childId]?.depth,
            zIndex: treeMaxDepth + 1 - nodes?.[childId]?.depth,
            node: nodes?.[childId]
          }))
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [nodeList, showLoading, selectedTreeSet.toString(), navItemHeight]
    )
    const rowRenderer = useMemo(
      () =>
        ({
          id,
          style,
          nodeInfo: {
            parentInfo: { isSticky },
            node,
            isSticky: isCurrentSticky,
            isItemSelected
          }
        }) => {
          return (showOnlySelected && isSticky) || !showOnlySelected ? (
            node.children?.length ? (
              <Menu
                key={id}
                className={classNames(classes.stickyRow, {
                  [classes.enhancedStickyRow]: isEnhancedViewEnabled
                })}
                style={{ ...style, paddingLeft: `${(node?.depth || 0) * 10}px` }}
                menuTitle={
                  <NavNodeName
                    node={node}
                    showLoading={showLoading}
                    isEnhancedViewEnabled={isEnhancedViewEnabled}
                    hasChildren={true}
                  />
                }
                onTitleClick={() =>
                  !showLoading &&
                  onNavClick({ id, leafNodeId: getLeafNode(nodeList, id) })
                }
              />
            ) : (
              <MenuItem
                key={id}
                className={classNames(classes.stickyRow, {
                  [classes.enhancedStickyRow]: isEnhancedViewEnabled
                })}
                style={{
                  ...style,
                  paddingLeft: `${
                    (node?.depth || 0) * ((node?.depth || 0) < 2 ? 20 : 10)
                  }px`
                }}
                onTitleClick={() =>
                  !showLoading &&
                  onNavClick({ id, leafNodeId: getLeafNode(nodeList, id) })
                }>
                {isEnhancedViewEnabled ? (
                  !showLoading && showSelectedIcon && isItemSelected ? (
                    <EnhancedSelectedIcon />
                  ) : null
                ) : !showLoading && showSelectedIcon && isCurrentSticky ? (
                  <SelectedIcon />
                ) : null}
                <NavNodeName
                  node={node}
                  showLoading={showLoading}
                  isEnhancedViewEnabled={isEnhancedViewEnabled}
                  hasChildren={false}
                />
              </MenuItem>
            )
          ) : null
        },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [nodeList, showLoading, selectedTreeSet.toString()]
    )
    return showLoading ? (
      <div>
        <StickyTree
          root={
            {
              id: 'root',
              height: 0,
              isSticky: true,
              top: 0,
              zIndex: treeMaxDepth + 1
            } as any
          }
          height={height || 150}
          getChildren={getLoaderChildren}
          rowRenderer={rowRenderer as any}
          renderRoot={false}
          scrollIndex={0}
          overscanRowCount={20}
          // Added below line just to fool compiler for width param
          {...({} as any)}
        />
      </div>
    ) : (
      <StickyTree
        ref={setTreeRef}
        root={
          {
            id: 'root',
            height: 0,
            isSticky: true,
            top: 0,
            zIndex: treeMaxDepth + 1
          } as any
        }
        height={height || 150}
        getChildren={getChildren}
        rowRenderer={rowRenderer as any}
        renderRoot={false}
        scrollIndex={showLoading ? 0 : scrollIndex}
        overscanRowCount={20}
        // Added below line just to fool compiler for width param
        {...({} as any)}
      />
    )
  })
}
